import { useState } from 'react';
import styles from '../../pages/MeetingReport.module.css';

export default function AverageTalkTimeChart({ participation, colors, nameMap = {}, printMode = false }) {
  const [mode, setMode] = useState('avg'); // 'avg' | 'med'

  const REF_20 = 20000;  // 20s in ms
  const REF_30 = 30000;  // 30s in ms

  const data = participation.speakers
    .map((p) => {
      const avg = p.turns > 0 ? Math.round(p.ms / p.turns) : 0;
      const displayName = nameMap[p.name] || p.name.split('@')[0];
      return { name: displayName, ms: avg };
    })
    .sort((a, b) => b.ms - a.ms)
    .map((d, i) => ({ ...d, color: colors[i % colors.length] }));

  const maxMs = Math.max(...data.map(d => d.ms), REF_30 * 1.3);

  // Chart dimensions
  const CHART_HEIGHT = 220;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;
  const BAR_WIDTH = printMode ? 28 : isMobile ? 22 : 36;
  const BAR_GAP = printMode ? 48 : isMobile ? 10 : 24;
  const Y_AXIS_WIDTH = 40;
  const chartWidth = Math.max(data.length * (BAR_WIDTH + BAR_GAP) + Y_AXIS_WIDTH + 40, printMode ? 520 : isMobile ? 200 : 300);

  // Y-axis ticks: 0, 15s, 30s, 45s, 60s
  const ticksMs = [0, 15000, 30000, 45000, 60000].filter(t => t <= maxMs * 1.1);
  if (!ticksMs.includes(60000) && maxMs > 45000) ticksMs.push(60000);

  const toY = (ms) => CHART_HEIGHT - Math.round((ms / maxMs) * CHART_HEIGHT);

  const fmtTick = (ms) => {
    const s = Math.round(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className={styles.chartContainer} style={printMode ? { margin: 0, height: '100%', display: 'flex', flexDirection: 'column' } : undefined}>
      {!printMode && (
        <div className={styles.chartHeaderRow}>
          <div className="report-section-header" style={{ margin: 0 }}>Average Talk Time</div>
          <div className={styles.toggleRow}>
            <span style={{ color: mode === 'avg' ? 'var(--navy)' : 'var(--ink-muted)', fontWeight: mode === 'avg' ? 700 : 400 }}>Avg</span>
            <div
              className={styles.toggleTrack}
              onClick={() => setMode(m => m === 'avg' ? 'med' : 'avg')}
              style={{ background: mode === 'med' ? 'var(--navy)' : 'var(--border)' }}
            >
              <div className={styles.toggleThumb} style={{ left: mode === 'med' ? 21 : 3 }} />
            </div>
            <span style={{ color: mode === 'med' ? 'var(--navy)' : 'var(--ink-muted)', fontWeight: mode === 'med' ? 700 : 400 }}>Med</span>
          </div>
        </div>
      )}
      <p className={styles.chartDescLg} style={printMode ? { margin: '0 0 6px', flexShrink: 0 } : undefined}>
        Target range: 20–30 seconds per speaking segment
      </p>

      <div className={printMode ? undefined : styles.overflowXAuto} style={printMode ? { width: '100%', flex: 1, minHeight: 0 } : undefined}>
        <svg
          viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT + 80}`}
          width="100%"
          height={printMode ? '100%' : CHART_HEIGHT + 80}
          style={{ display: 'block' }}
        >
          {/* Y-axis ticks + gridlines */}
          {ticksMs.map(t => {
            const y = toY(t);
            return (
              <g key={t}>
                <line x1={Y_AXIS_WIDTH} y1={y} x2={chartWidth} y2={y}
                  stroke="var(--border)" strokeWidth={1} />
                <text x={Y_AXIS_WIDTH - 6} y={y + 4}
                  textAnchor="end" fontSize={10} fill="var(--ink-muted)">
                  {fmtTick(t)}
                </text>
              </g>
            );
          })}

          {/* 20s reference line */}
          {REF_20 <= maxMs && (
            <g>
              <line
                x1={Y_AXIS_WIDTH} y1={toY(REF_20)}
                x2={chartWidth - 36} y2={toY(REF_20)}
                stroke="#4cc9f0" strokeWidth={1.5} strokeDasharray="5 4"
              />
              <text x={chartWidth - 34} y={toY(REF_20) + 4}
                fontSize={10} fontWeight={700} fill="#4cc9f0">
                20s
              </text>
            </g>
          )}

          {/* 30s reference line */}
          {REF_30 <= maxMs && (
            <g>
              <line
                x1={Y_AXIS_WIDTH} y1={toY(REF_30)}
                x2={chartWidth - 36} y2={toY(REF_30)}
                stroke="#4cc9f0" strokeWidth={1.5} strokeDasharray="5 4"
              />
              <text x={chartWidth - 34} y={toY(REF_30) + 4}
                fontSize={10} fontWeight={700} fill="#4cc9f0">
                30s
              </text>
            </g>
          )}

          {/* Bars */}
          {data.map((d, i) => {
            const barH = Math.max(CHART_HEIGHT - toY(d.ms), 2);
            const x = Y_AXIS_WIDTH + 10 + i * (BAR_WIDTH + BAR_GAP);
            const y = toY(d.ms);
            return (
              <g key={d.name}>
                <rect
                  x={x} y={y}
                  width={BAR_WIDTH} height={barH}
                  fill={d.color} rx={4} ry={4}
                />
                {/* X-axis label */}
                <text
                  x={x + BAR_WIDTH / 2} y={CHART_HEIGHT + 14}
                  textAnchor="end" fontSize={11} fill="var(--ink-muted)"
                  transform={`rotate(-45, ${x + BAR_WIDTH / 2}, ${CHART_HEIGHT + 14})`}>
                  {d.name}
                </text>
              </g>
            );
          })}

          {/* X axis baseline */}
          <line x1={Y_AXIS_WIDTH} y1={CHART_HEIGHT} x2={chartWidth} y2={CHART_HEIGHT}
            stroke="var(--border)" strokeWidth={1} />
        </svg>
      </div>
    </div>
  );
}
